import type { BaseQueryApi } from '@reduxjs/toolkit/query';

import type { QueueViewKey } from '../types/encounterWork';
import { appendQueueRunPage, EMPTY_QUEUE_RUN, type QueueRun } from '../utils/queueRun';

import { readQueueRunPage } from './queueRunPageQuery';
import { billingV2Api } from './v2-api';

/**
 * The queue run behind a Home card, as one cache entry (GDN-3379).
 *
 * `queryFn` reads ONE page and returns: the first page for a new run, so a
 * click leaves the moment it lands, or the page after the last one read for
 * a run that stopped on an error, so a Retry resumes rather than restarts.
 * The walk through the remaining pages rides `onQueryStarted`, which —
 * unlike `onCacheEntryAdded`, called once per entry — runs for a refetch
 * too, and is therefore what lets a Retry of the same args carry on where
 * the walk stopped. The walker appends each page with `updateCachedData`,
 * marks the run complete, and steps aside for a newer fetch of the same
 * entry. `onCacheEntryAdded` has one job: cutting the reads short when the
 * entry is removed.
 *
 * FROZEN (GDN-3322): the entry is append-only and provides no tags, so no
 * mutation refetches or drops it, and a position derived against it stays
 * true while it grows. A new Home mount takes a new `runId` — a new entry —
 * rather than re-reading this one.
 */
export interface QueueRunArgs {
  queue: QueueViewKey;
  runId: string;
}

const QUEUE_RUN_ENDPOINT = 'getQueueRun';

/** The cache key, spelled out so a run can be read back without its own selector. */
export const queueRunCacheKey = ({ queue, runId }: QueueRunArgs): string =>
  `${QUEUE_RUN_ENDPOINT}(${queue}:${runId})`;

type ApiSlice = Record<typeof billingV2Api.reducerPath, ReturnType<typeof billingV2Api.reducer>>;

/** The run the cache already holds for these args, if any. */
function heldQueueRun(state: unknown, args: QueueRunArgs): QueueRun | undefined {
  const entry = (state as ApiSlice)[billingV2Api.reducerPath].queries[queueRunCacheKey(args)];
  return entry?.data as QueueRun | undefined;
}

/** The walk under way per entry, so a removed entry ends its reads and a newer fetch takes over an older walker's. */
const walks = new Map<string, AbortController>();

function startWalk(key: string): AbortController {
  walks.get(key)?.abort();
  const controller = new AbortController();
  walks.set(key, controller);
  return controller;
}

function endWalk(key: string, controller: AbortController): void {
  if (walks.get(key) === controller) walks.delete(key);
}

export const queueRunApi = billingV2Api.injectEndpoints({
  endpoints: build => ({
    getQueueRun: build.query<QueueRun, QueueRunArgs>({
      serializeQueryArgs: ({ queryArgs }) => queueRunCacheKey(queryArgs),

      queryFn: async (args, api) => {
        const held = heldQueueRun(api.getState(), args);
        /* A finished run has nothing left to read. */
        if (held?.complete) return { data: held };
        /* Where the walk stopped, or the first page for a run that has none. */
        const cursor = held?.nextCursor ?? null;
        const page = await readQueueRunPage(args.queue, cursor, api);
        if (page.error) {
          /* No first page is no run: the query fails and the click falls
             back to the card's rows. A later page's failure keeps what loaded. */
          return held ? { data: { ...held, error: page.error } } : { error: page.error };
        }
        return {
          data: appendQueueRunPage(held ?? EMPTY_QUEUE_RUN, args.queue, page.data, cursor),
        };
      },

      async onQueryStarted(
        args,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { dispatch, extra, getCacheEntry, getState, queryFulfilled, requestId, updateCachedData },
      ) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        const key = queueRunCacheKey(args);
        const walk = startWalk(key);
        const api: BaseQueryApi = {
          abort: reason => walk.abort(reason),
          dispatch,
          endpoint: QUEUE_RUN_ENDPOINT,
          extra,
          getState,
          signal: walk.signal,
          type: 'query',
        };
        try {
          for (;;) {
            const before = getCacheEntry();
            /* This fetch's walker, and only this one: a newer fetch of the
               same entry, or the entry's removal, ends it. */
            if (before.requestId !== requestId || !before.data) return;
            const { complete, error, nextCursor } = before.data;
            if (complete || error || !nextCursor) return;
            const page = await readQueueRunPage(args.queue, nextCursor, api);
            const after = getCacheEntry();
            if (after.requestId !== requestId || !after.data) return;
            const run = after.data;
            const next = page.error
              ? { ...run, error: page.error }
              : appendQueueRunPage(run, args.queue, page.data, nextCursor);
            updateCachedData(() => next);
            if (page.error) return;
          }
        } finally {
          endWalk(key, walk);
        }
      },

      async onCacheEntryAdded(args, { cacheEntryRemoved }) {
        await cacheEntryRemoved;
        const key = queueRunCacheKey(args);
        walks.get(key)?.abort();
        walks.delete(key);
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetQueueRunQuery } = queueRunApi;

export const selectQueueRun = (args: QueueRunArgs) =>
  queueRunApi.endpoints.getQueueRun.select(args);

/** The state shape the run selector reads: the api slice, however the store around it is typed. */
export type QueueRunApiState = Parameters<ReturnType<typeof selectQueueRun>>[0];

/** Nothing more will land without a retry: the run is complete, stopped on a page, never started, or is gone. */
export const isQueueRunSettled = (entry: {
  data?: QueueRun;
  isError: boolean;
  isUninitialized: boolean;
}): boolean =>
  entry.isUninitialized ||
  entry.isError ||
  Boolean(entry.data?.complete) ||
  entry.data?.error != null;
